import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxColor,
  OnyxIconName,
  OnyxScheduleCompliance,
} from '@onyx/angular';
import { OnyxIconComponent, OnyxTooltipDirective } from '@onyx/angular';
import { ScheduleComplianceHelper } from '../../../../helpers/schedule-compliance.helper';
import { OrderPointStatus } from '../../../../../dashboard/orders/common/enums/order-point-status';
import { CommonHelper } from '../../../../helpers/common.helper';
import { RouteHelper } from '../../../../helpers/route.helper';
import type { RoutePoint } from '../../../../services/route.service';
import { RouteService } from '../../../../services/route.service';

@Component({
  selector: 'app-route-point-marker',
  imports: [OnyxIconComponent, OnyxTooltipDirective, NgTemplateOutlet],
  templateUrl: './route-point-marker.component.html',
  styleUrl: './route-point-marker.component.scss',
})
export class RoutePointMarkerComponent {
  private readonly routeService = inject(RouteService);
  private readonly translateService = inject(TranslateService);

  public readonly size = input.required<24 | 32>();
  public readonly icon = input<OnyxIconName>();
  public readonly iconSize = input<number | null>();
  public readonly expanded = input<boolean>();
  public readonly point = input<RoutePoint>();
  public readonly isPoint = input(true);
  public readonly compliance = input<OnyxScheduleCompliance | null>();
  public readonly letter = input<string | null>();
  public readonly pointIndex = input<number | null>();
  public readonly isDelegation = input(false);
  public readonly dissolveEffect = input(true);
  public readonly forceIcons = input(false);
  public readonly markerColor = input<OnyxColor | null>(null);

  protected readonly OrderPointStatus = OrderPointStatus;
  protected readonly Math = Math;

  protected proposition = computed(() =>
    this.point() ? RouteHelper.isProposition(this.point()!) : false,
  );
  protected isHandlingPoint = computed(() =>
    this.point()
      ? RouteHelper.isHandlingPoint(this.point()!) &&
        this.status() === OrderPointStatus.IN_PROGRESS
      : false,
  );
  protected color = computed<OnyxColor | null>(() =>
    this.compliance()
      ? ScheduleComplianceHelper.getColor(this.compliance()!)
      : null,
  );
  protected status = computed(() =>
    this.point() ? RouteHelper.getStatus(this.point()!) : null,
  );
  protected action = computed(() =>
    RouteHelper.getPointAction(
      this.point(),
      this.status(),
      this.compliance(),
      this.routeService,
      this.translateService,
      true,
    ),
  );
  protected pointLetter = computed(() => {
    if (this.routeService.context === 'order') return null;
    if (this.forceIcons()) return null;
    if (this.letter()) return this.letter();

    const pointIndex = this.pointIndex();
    if (pointIndex != null) return CommonHelper.getAlphabetLetter(pointIndex);

    const point = this.point();
    if (!point) return null;

    const cycleIndex = this.routeService.getCycleIndex(point);
    if (cycleIndex === -1) return null;

    return this.isPoint() ? CommonHelper.getAlphabetLetter(cycleIndex) : null;
  });
}
